/**
 * Widget management and initialization for Supernal TTS Widget
 */

import { WidgetConfig } from './types';
import { scrubText } from './core/text-processor';
import { CacheManager } from './core/cache';
import { UIComponentFactory } from './ui/components';
import { UIStateManager } from './ui/state-manager';
import { AudioGenerator } from './audio/generator';
import { AudioPlayer } from './audio/player';

export class WidgetManager {
  private defaultVoice: string;
  private defaultProvider: string;
  private defaultSpeed: number;
  private apiKey?: string;
  private clientSideSpeed: boolean;
  private generationMode: 'standard' | 'progressive' | 'realtime';
  private progressiveThreshold: number;
  private isProcessing: boolean = false;

  private cacheManager: CacheManager;
  private uiFactory: UIComponentFactory;
  private uiStateManager: UIStateManager;
  private audioGenerator: AudioGenerator;
  private audioPlayer: AudioPlayer;

  constructor(
    config: {
      defaultVoice: string;
      defaultProvider: string;
      defaultSpeed: number;
      apiKey?: string;
      clientSideSpeed: boolean;
      generationMode: 'standard' | 'progressive' | 'realtime';
      progressiveThreshold: number;
    },
    modules: {
      cacheManager: CacheManager;
      uiFactory: UIComponentFactory;
      uiStateManager: UIStateManager;
      audioGenerator: AudioGenerator;
      audioPlayer: AudioPlayer;
    }
  ) {
    this.defaultVoice = config.defaultVoice;
    this.defaultProvider = config.defaultProvider;
    this.defaultSpeed = config.defaultSpeed;
    this.apiKey = config.apiKey;
    this.clientSideSpeed = config.clientSideSpeed;
    this.generationMode = config.generationMode;
    this.progressiveThreshold = config.progressiveThreshold;

    this.cacheManager = modules.cacheManager;
    this.uiFactory = modules.uiFactory;
    this.uiStateManager = modules.uiStateManager;
    this.audioGenerator = modules.audioGenerator;
    this.audioPlayer = modules.audioPlayer;
  }

  /**
   * Initialize widget discovery and DOM observation
   */
  public init(): void {
    // Auto-initialize widgets on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initializeWidgets());
    } else {
      this.initializeWidgets();
    }

    // Set up MutationObserver to detect dynamically added widgets
    // This handles client-side routing (SPA navigation) and dynamic content
    this.observeDOM();
  }

  /**
   * Observe DOM for dynamically added widgets
   */
  private observeDOM(): void {
    // Watch for new elements being added to the DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          // Check if the added node is an element
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;

            // Check if it's a widget or contains widgets
            if (element.classList?.contains('supernal-tts-widget')) {
              this.setupWidget(element);
            } else {
              // Check for widgets within the added element
              const widgets = element.querySelectorAll?.('.supernal-tts-widget');
              widgets?.forEach((widget) => this.setupWidget(widget as HTMLElement));
            }
          }
        });
      });
    });

    // Start observing the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Initialize all widgets on the page
   */
  public initializeWidgets(): void {
    const widgets = document.querySelectorAll<HTMLElement>('.supernal-tts-widget');
    widgets.forEach(widget => this.setupWidget(widget));
  }

  /**
   * Setup a single widget element
   */
  public setupWidget(element: HTMLElement): void {
    // Check if already initialized to prevent duplicates
    if (element.classList.contains('supernal-tts-widget-initialized')) {
      return;
    }

    // Parse widget configuration from data attributes
    const config = this.parseWidgetConfig(element);

    if (!config.text) {
      console.warn('TTS Widget: No text found for widget', element);
      return;
    }

    // Generate hash for caching
    const hash = this.cacheManager.generateHash(config.text, {
      voice: config.voice,
      provider: config.provider,
      speed: config.speed
    });

    // Use modular widget for all widgets
    this.setupModularWidget(element, config, hash);
  }

  /**
   * Parse widget configuration from data attributes
   */
  private parseWidgetConfig(element: HTMLElement): WidgetConfig {
    const rawText = element.dataset.text || element.textContent?.trim() || '';
    const text = scrubText(rawText);
    const voice = element.dataset.voice || this.defaultVoice;
    const provider = element.dataset.provider || this.defaultProvider;
    const speed = element.dataset.speed ? parseFloat(element.dataset.speed) : this.defaultSpeed;

    // Parse voices list (comma-separated)
    const voicesAttr = element.dataset.voices || '';
    const voices = voicesAttr ? voicesAttr.split(',').map(v => v.trim()).filter(Boolean) : [];

    // Parse boolean flags
    const enableSpeed = element.dataset.enableSpeed === 'true';
    const enableProgress = element.dataset.enableProgress === 'true';

    // Parse alignment (default: 'left')
    const align = (element.dataset.align === 'center' || element.dataset.align === 'left')
      ? element.dataset.align
      : 'left';

    // Check for widget-specific API key (overrides global)
    const apiKey = element.dataset.apiKey || this.apiKey;

    return {
      text,
      voices,
      enableSpeed,
      enableProgress,
      speed,
      provider,
      voice,
      apiKey,
      align,
    };
  }

  /**
   * Setup modular widget with conditional features based on config
   */
  private setupModularWidget(element: HTMLElement, config: WidgetConfig, hash: string): void {
    let currentVoice = config.voice;
    let currentSpeed = config.speed;
    const { text, voices, enableSpeed, enableProgress, provider, apiKey, align } = config;
    let savedPosition: number = 0; // Track playback position for voice/speed changes

    // Create main container
    const container = document.createElement('div');
    container.className = 'supernal-tts-modular-widget';

    // Add alignment class
    if (align === 'center') {
      container.classList.add('supernal-tts-align-center');
    } else {
      container.classList.add('supernal-tts-align-left');
    }

    // Create unified circle (default: single circle with ~+)
    const unifiedCircle = this.uiFactory.createUnifiedCircle();
    container.appendChild(unifiedCircle);

    // Create play button (will be used in split circles)
    const playButton = this.uiFactory.createCircularPlayButton();

    // Make unified circle clickable - delegates to play button
    unifiedCircle.addEventListener('click', (e) => {
      e.preventDefault();
      playButton.click();
    });

    // Voice dropdown (conditional) - create BEFORE split circles
    let voiceDropdown: HTMLElement | null = null;
    if (voices.length > 0) {
      voiceDropdown = this.uiFactory.createVoiceDropdown(voices, currentVoice);

      // Listen for voice changes
      voiceDropdown.addEventListener('voice-changed', ((e: CustomEvent) => {
        const newVoice = e.detail.voice;

        // Expand controls permanently when voice is changed
        container.classList.add('permanently-expanded');
        const expandFn = (container.querySelector('.supernal-tts-split-circles') as any)?._expandControlsPermanently;
        if (expandFn) {
          expandFn();
        }

        // If voice changed while playing, save position and regenerate
        if (newVoice !== currentVoice && this.audioPlayer.isPlayingOn(playButton)) {
          currentVoice = newVoice;

          // Save current playback position as percentage
          savedPosition = this.audioPlayer.getCurrentPosition();

          // Get the progress bar reference
          const widgetData = (element as any)._ttsWidget;
          const progressBar = widgetData?.progressBar;

          // Clean stop: clear audio and intervals, but keep progress bar visible
          this.audioPlayer.cleanStopAudioKeepProgress((state) => {
            this.uiStateManager.setButtonState(playButton, state);
          });

          // Trigger new playback with updated voice and saved position
          setTimeout(() => {
            this.handlePlayClickFromPosition(playButton, text, {
              voice: currentVoice,
              provider,
              speed: currentSpeed,
              hash: this.cacheManager.generateHash(text, { voice: currentVoice, provider, speed: currentSpeed }),
              progressBar,
              apiKey
            }, savedPosition);
          }, 50);
        } else {
          currentVoice = newVoice;
        }
      }) as EventListener);
    }

    // Speed dropdown (conditional) - create BEFORE split circles
    let speedDropdown: HTMLElement | null = null;
    if (enableSpeed) {
      speedDropdown = this.uiFactory.createSpeedDropdown(currentSpeed);

      // Listen for speed changes
      speedDropdown.addEventListener('speed-changed', ((e: CustomEvent) => {
        const newSpeed = e.detail.speed;

        // Expand controls permanently when speed is changed
        container.classList.add('permanently-expanded');
        const expandFn = (container.querySelector('.supernal-tts-split-circles') as any)?._expandControlsPermanently;
        if (expandFn) {
          expandFn();
        }

        if (newSpeed !== currentSpeed) {
          currentSpeed = newSpeed;

          // Client-side speed adjustment: use HTML5 playbackRate with pitch preservation
          if (this.clientSideSpeed && this.audioPlayer.isPlayingOn(playButton)) {
            // Instant speed change via playbackRate with pitch preservation - no regeneration needed!
            // The browser's time-stretching algorithm maintains pitch quality
            this.audioPlayer.setPlaybackRate(newSpeed);
          }
          // Server-side speed adjustment: regenerate audio (expensive but higher quality)
          else if (!this.clientSideSpeed && this.audioPlayer.isPlayingOn(playButton)) {
            // Save current playback position as percentage
            savedPosition = this.audioPlayer.getCurrentPosition();

            // Get the progress bar reference
            const widgetData = (element as any)._ttsWidget;
            const progressBar = widgetData?.progressBar;

            // Clean stop: clear audio and intervals, but keep progress bar visible
            this.audioPlayer.cleanStopAudioKeepProgress((state) => {
              this.uiStateManager.setButtonState(playButton, state);
            });

            // Trigger new playback with updated speed and saved position
            setTimeout(() => {
              this.handlePlayClickFromPosition(playButton, text, {
                voice: currentVoice,
                provider,
                speed: currentSpeed,
                hash: this.cacheManager.generateHash(text, { voice: currentVoice, provider, speed: currentSpeed }),
                progressBar,
                apiKey
              }, savedPosition);
            }, 50);
          }
        }
      }) as EventListener);
    }

    // Create split circles layout (revealed on hover: ~ ▶ voice speed +)
    const splitCircles = this.uiFactory.createSplitCircles(playButton, voiceDropdown, speedDropdown);
    container.appendChild(splitCircles);

    // Progress bar (conditional)
    let progressBar: HTMLElement | null = null;
    if (enableProgress) {
      progressBar = this.uiFactory.createProgressBar(
        (audio: HTMLAudioElement, percentage: number) => {
          audio.currentTime = (percentage / 100) * audio.duration;
        },
        () => this.audioPlayer.getCurrentAudio()
      );
      container.appendChild(progressBar);
    }

    // IMPORTANT: Prepend controls, keep original content
    element.insertBefore(container, element.firstChild);
    element.classList.add('supernal-tts-widget-initialized', 'supernal-tts-widget-modular');

    // Add has-progress class if progress bar is enabled
    if (enableProgress) {
      element.classList.add('has-progress');
    }

    // Now that container is in DOM, attach the mouseleave handler to the modular widget container
    // Reset controls when mouse leaves the entire widget
    const resetControls = (splitCircles as any)._resetControls;
    const expandControlsPermanently = (splitCircles as any)._expandControlsPermanently;
    if (resetControls) {
      container.addEventListener('mouseleave', resetControls);
    }

    // Setup play button handler
    playButton.addEventListener('click', (e) => {
      e.preventDefault();

      // Expand controls permanently after play is clicked
      container.classList.add('permanently-expanded');

      // Also call the expand function to update the controls state
      if (expandControlsPermanently) {
        expandControlsPermanently();
      }

      const newHash = this.cacheManager.generateHash(text, { voice: currentVoice, provider, speed: currentSpeed });
      this.handlePlayClick(playButton, text, {
        voice: currentVoice,
        provider,
        speed: currentSpeed,
        hash: newHash,
        progressBar,
        apiKey
      });
    });

    // Store reference
    (element as any)._ttsWidget = {
      text,
      voice: currentVoice,
      provider,
      speed: currentSpeed,
      hash,
      playButton,
      voiceDropdown,
      speedDropdown,
      progressBar
    };
  }

  /**
   * Handle play button click
   */
  private async handlePlayClick(button: HTMLButtonElement, text: string, options: any): Promise<void> {
    const { hash, progressBar } = options;

    // If currently playing this button, pause it
    if (this.audioPlayer.isPlayingOn(button)) {
      this.audioPlayer.pauseAudio((state) => {
        this.uiStateManager.setButtonState(button, state);
      });
      return;
    }

    // If currently paused on this button, resume it
    if (this.audioPlayer.isPausedOn(button)) {
      this.audioPlayer.resumeAudio((state) => {
        this.uiStateManager.setButtonState(button, state);
      });
      return;
    }

    // Don't allow starting new audio while processing
    if (this.isProcessing) {
      return;
    }

    // If another button is playing, stop it first
    const currentButton = this.audioPlayer.getCurrentButton();
    if (currentButton && currentButton !== button) {
      this.audioPlayer.stopAudio((state) => {
        this.uiStateManager.setButtonState(currentButton, state);
      });
    }

    try {
      this.isProcessing = true;

      // Update button state
      this.uiStateManager.setButtonState(button, 'loading');

      // Check cache first
      let audioUrl = this.cacheManager.getCachedAudioUrl(hash);

      if (!audioUrl) {
        // Determine if we should use progressive mode
        const useProgressive = (
          this.generationMode === 'progressive' ||
          (this.generationMode === 'standard' && text.length > this.progressiveThreshold)
        );

        if (useProgressive) {
          // Use progressive SSE streaming
          const result = await this.audioGenerator.generateAudioProgressive(
            text,
            options,
            button,
            progressBar,
            (state) => this.uiStateManager.setButtonState(button, state)
          );

          audioUrl = result.audioUrl;

          // Cache the result
          this.cacheManager.cacheAudioUrl(hash, audioUrl, result);
        } else {
          // Standard generation
          const response = await this.audioGenerator.generateAudio(text, options);
          audioUrl = response.audioUrl;

          // Cache the result
          this.cacheManager.cacheAudioUrl(hash, audioUrl, response);
        }
      }

      // Play audio with progress bar updates
      await this.audioPlayer.playAudio(
        audioUrl,
        button,
        (state) => this.uiStateManager.setButtonState(button, state),
        (audio, pb) => this.uiStateManager.updateProgressBar(audio, pb),
        progressBar,
        options.speed,
        hash
      );

      this.isProcessing = false;
    } catch (error) {
      console.error('TTS Error:', error);
      this.uiStateManager.setButtonState(button, 'error');

      // Show user-friendly error
      this.uiStateManager.showError(button, (error as Error).message);

      setTimeout(() => this.uiStateManager.setButtonState(button, 'ready'), 3000);
      this.isProcessing = false;
    }
  }

  /**
   * Handle play click and seek to a specific position (used for voice/speed changes)
   */
  private async handlePlayClickFromPosition(button: HTMLButtonElement, text: string, options: any, positionPercent: number): Promise<void> {
    const { hash, progressBar } = options;

    // Don't allow starting new audio while processing
    if (this.isProcessing) {
      return;
    }

    try {
      this.isProcessing = true;

      // Update button state
      this.uiStateManager.setButtonState(button, 'loading');

      // Check cache first
      let audioUrl = this.cacheManager.getCachedAudioUrl(hash);

      if (!audioUrl) {
        // Generate new audio
        const response = await this.audioGenerator.generateAudio(text, options);
        audioUrl = response.audioUrl;

        // Cache the result
        this.cacheManager.cacheAudioUrl(hash, audioUrl, response);
      }

      // Play audio with progress bar updates and seek to saved position
      await this.audioPlayer.playAudioFromPosition(
        audioUrl,
        button,
        (state) => this.uiStateManager.setButtonState(button, state),
        (audio, pb) => this.uiStateManager.updateProgressBar(audio, pb),
        progressBar,
        positionPercent,
        hash
      );

      this.isProcessing = false;
    } catch (error) {
      console.error('TTS Error:', error);
      this.uiStateManager.setButtonState(button, 'error');

      // Show user-friendly error
      this.uiStateManager.showError(button, (error as Error).message);

      setTimeout(() => this.uiStateManager.setButtonState(button, 'ready'), 3000);
      this.isProcessing = false;
    }
  }

  /**
   * Add a widget programmatically
   */
  public addWidget(element: HTMLElement | string, text: string, options: any = {}): void {
    let el: HTMLElement | null = null;

    if (typeof element === 'string') {
      el = document.querySelector<HTMLElement>(element);
    } else {
      el = element;
    }

    if (!el) {
      console.error('TTS Widget: Element not found');
      return;
    }

    el.classList.add('supernal-tts-widget');
    el.dataset.text = scrubText(text);

    if (options.voice) el.dataset.voice = options.voice;
    if (options.provider) el.dataset.provider = options.provider;

    this.setupWidget(el);
  }
}
